import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import {
  sx, ReportHeader, ReportBodySections, normalizeReportData,
  fmtDate, fmtWhen, fmtEditWindow,
} from '../pages/reportShared'
import AuditTrail, { outOfWeekEntry } from '../pages/reportAudit'
import HoursProofingModal from './HoursProofingModal'

// In-app Field Reports tab (desktop + mobile). Reads daily_field_reports via
// the session-authed /api/field-reports endpoint. OWNERS additionally get row +
// bulk controls: reopen, edit, delete, restore. A named office delegate (Carla —
// REOPEN_DELEGATES in api/lib/admin.js) gets REOPEN ONLY: row checkboxes, a ⋯
// menu of Download PDF + Reopen on approved rows, and "Reopen Selected". Her
// reopens email the owners. Other office `admin` users get no bulk UI.
// Keep the isAdmin rule below in sync with isAdminRole() in api/lib/admin.js;
// the reopen right is server-decided (`can_reopen` on the list response).

// Navigate to a report page, first rewriting the current history entry from
// "/" to "/?tab=field-reports". The tabs are component state, not routes, so
// a plain browser-back to "/" would land on the default board tab; with the
// rewrite, back returns to a URL the Dashboard/MobileDashboard deep-link
// handlers restore (and sessionStorage restores the filters). replaceState,
// not pushState — we're correcting the current entry, not adding one.
function gotoReportPage(url) {
  try { window.history.replaceState(window.history.state, '', '/?tab=field-reports') } catch (e) { /* history unavailable — navigate anyway */ }
  window.location.href = url
}

// Monday (YYYY-MM-DD) of the week containing a given date (UTC parse, no drift —
// matches the server's week math in api/payroll/csv-builder.js).
function mondayOf(ymd) {
  const [y, m, d] = ymd.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d))
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7))
  return t.toISOString().slice(0, 10)
}
// Sunday (WEEK-ENDING date) of the Mon–Sun week containing a given date.
// Cornerstone identifies payroll weeks by their week-ending date, so this is
// what the picker + filenames show. mondayOf() is the exact inverse — mondayOf
// of a Sunday is that same week's Monday, i.e. the Sunday minus 6 days — and
// stays the week_start API param. Picking ANY day snaps to that week's Sunday.
function sundayOf(ymd) {
  const [y, m, d] = mondayOf(ymd).split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d))
  t.setUTCDate(t.getUTCDate() + 6)
  return t.toISOString().slice(0, 10)
}
// 'M/D' for a YYYY-MM-DD, for compact inline week labels.
function mdOf(ymd) {
  const [, m, d] = ymd.split('-').map(Number)
  return `${m}/${d}`
}
// Today in America/New_York as 'YYYY-MM-DD' (en-CA formats ISO-style). The ONE
// definition of "today" on this screen — a bare `new Date()` would drift to
// tomorrow's date for anyone clicking after 8 PM ET.
function todayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
}
// Shift a 'YYYY-MM-DD' by n days (UTC parse, no drift).
function shiftDays(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d))
  t.setUTCDate(t.getUTCDate() + n)
  return t.toISOString().slice(0, 10)
}
// First / last day of the month `offset` months from the one containing `ymd`.
// The end uses day 0 of the FOLLOWING month, so Feb (incl. leap years) and the
// December→January rollover come out right without any special-casing.
function monthStart(ymd, offset = 0) {
  const [y, m] = ymd.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1 + offset, 1)).toISOString().slice(0, 10)
}
function monthEnd(ymd, offset = 0) {
  const [y, m] = ymd.split('-').map(Number)
  return new Date(Date.UTC(y, m + offset, 0)).toISOString().slice(0, 10)
}
// Most-recently-completed payroll week's Monday (the cron's default week).
function lastCompletedWeekMonday() {
  return shiftDays(mondayOf(todayET()), -7)
}

// ── list-filter persistence ───────────────────────────────────────────────────
// Opening a report navigates to /report/approve as a FULL PAGE LOAD, so this
// component unmounts and its filter state dies — coming back via "Back to Field
// Reports" would otherwise dump the user on an unfiltered list.
//
// sessionStorage, NOT localStorage, is deliberate: it is per-tab and clears when
// the tab closes, so a date range set today can never silently narrow the list
// days later. Do NOT switch to localStorage.
const FILTERS_KEY = 'fieldReports.filters.v1'
const FILTER_DEFAULTS = { from: '', to: '', status: 'all', job: 'all' }

// Read + validate the saved shape FIELD BY FIELD, so a hand-edited or
// stale-format entry can't put a non-string into a filter the list compares
// against. Anything unexpected falls back to that field's default; a parse or
// storage failure returns null and every caller just keeps its own default.
function loadSavedFilters() {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY)
    if (!raw) return null
    const s = JSON.parse(raw)
    if (!s || typeof s !== 'object') return null
    const str = (v, d) => (typeof v === 'string' ? v : d)
    return {
      filters: {
        from: str(s.from, FILTER_DEFAULTS.from),
        to: str(s.to, FILTER_DEFAULTS.to),
        status: str(s.status, FILTER_DEFAULTS.status),
        job: str(s.job, FILTER_DEFAULTS.job),
      },
      quickRange: str(s.quickRange, ''),
      showDeleted: typeof s.showDeleted === 'boolean' ? s.showDeleted : false,
    }
  } catch (e) {
    return null
  }
}

// Quick-range presets for the From/To filters. Weeks are Mon–Sun via the
// existing mondayOf/sundayOf helpers — same week-ending-Sunday convention as
// the payroll CSV picker. Returns { from, to } ('' clears a bound), or null for
// the placeholder. Nothing here filters: it only fills the two date inputs, so
// the actual filtering stays the client-side compare in `filtered`.
const QUICK_RANGES = [
  ['', 'Quick range…'],
  ['this_week', 'This week'],
  ['last_week', 'Last week'],
  ['this_month', 'This month'],
  ['last_month', 'Last month'],
  ['this_year', 'This year'],
  ['all', 'All dates'],
]
function quickRangeDates(key) {
  const today = todayET()
  switch (key) {
    case 'this_week': return { from: mondayOf(today), to: sundayOf(today) }
    // Any day in the prior week snaps to that week's Mon/Sun — no week math here.
    case 'last_week': { const p = shiftDays(today, -7); return { from: mondayOf(p), to: sundayOf(p) } }
    case 'this_month': return { from: monthStart(today), to: monthEnd(today) }
    case 'last_month': return { from: monthStart(today, -1), to: monthEnd(today, -1) }
    case 'this_year': return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` }
    case 'all': return { from: '', to: '' }
    default: return null
  }
}

// Banner colors for an actionMsg by kind — ok (green), warn (amber: it worked,
// but read this), anything else (red). One helper so the three render sites
// can't drift.
const MSG_COLORS = {
  ok:   ['#eaf6ec', '#1e7e34', '#1e5128'],
  warn: ['#fff8e1', '#e0a800', '#8a5d00'],
}
function msgBanner(kind) {
  const [bg, bd, fg] = MSG_COLORS[kind] || ['#fdecec', '#d9534f', '#a12622']
  return sx.banner(bg, bd, fg)
}

// Single source of truth for a report's status: returns a stable `key` (used by
// the Status filter) alongside the rendered label/colors, so the filter and the
// labels can never drift apart.
function statusInfo(r) {
  if (r.deleted_at) return { key: 'deleted', label: 'Deleted', bg: '#eceff1', fg: '#607d8b' }
  if (r.pm_approval_status === 'approved') return { key: 'approved', label: 'Approved', bg: '#eaf6ec', fg: '#1e5128' }
  // Escalated but not yet approved — surface as escalated (run-escalations sets
  // pm_escalated_at/count rather than pm_approval_status, so check both).
  if (r.pm_approval_status === 'escalated' || r.pm_escalated_at || (r.pm_escalated_count || 0) > 0)
    return { key: 'escalated', label: 'Escalated', bg: '#fde4d6', fg: '#9a3412' }
  if (r.call_status === 'no_useful_data') {
    // A no_useful_data call with a transcript / real duration DID connect — the
    // foreman just hung up before a report was completed. Don't imply a phone
    // failure. Only call it "did not connect" when nothing was captured.
    const connected = !!(r.raw_transcript || r.transcript_english) || (r.call_duration_seconds || 0) >= 15
    const dur = fmtDur(r.call_duration_seconds)
    const label = connected
      ? (dur ? `Call connected (${dur}) — no report completed` : 'Call connected — no report completed')
      : 'No data — call did not connect'
    return { key: 'no_data', label, bg: '#eef0f2', fg: '#555' }
  }
  if (r.call_status && r.call_status !== 'completed') {
    const m = { failed: 'Call failed', no_answer_retrying: 'No answer (retrying)', no_answer_escalated: 'No answer' }
    return { key: 'call_failed', label: m[r.call_status] || 'Awaiting call', bg: '#eef0f2', fg: '#555' }
  }
  if (!r.report_data) return { key: 'extraction_failed', label: 'Extraction failed', bg: '#fdecec', fg: '#a12622' }
  // A report is only the PM's to review once the foreman has actually submitted
  // (voice or online). Until then it's unsubmitted: a job with NO foreman can
  // never self-submit and needs office entry; one with a foreman is just waiting.
  if (r.foreman_submitted_at) return { key: 'pending_pm', label: 'Pending PM Review', bg: '#fff4e5', fg: '#92510a' }
  if (!r.foreman_name && !r.foreman_email) return { key: 'no_foreman', label: 'No foreman — needs office entry', bg: '#f3e8ff', fg: '#6b21a8' }
  return { key: 'pending_foreman', label: 'Pending Foreman Submit', bg: '#eef3fb', fg: '#1f4e79' }
}
function fmtDur(s) { return s ? `${Math.floor(s / 60)}m ${s % 60}s` : null }
const isApproved = r => r.pm_approval_status === 'approved'
const isDeleted = r => !!r.deleted_at

function ConfirmModal({ title, body, label, danger, busy, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !busy) onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel, busy])
  return (
    <div onClick={() => !busy && onCancel()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 460, width: '100%', padding: '22px 22px 18px', boxShadow: '0 10px 40px rgba(0,0,0,.25)' }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#1e2d3d', marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: '#444', lineHeight: 1.55, marginBottom: 20 }}>{body}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onCancel} disabled={busy}
            style={{ padding: '9px 16px', fontSize: 14, border: '1px solid #cfd6dd', borderRadius: 8, background: '#fff', cursor: busy ? 'not-allowed' : 'pointer' }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy}
            style={{ padding: '9px 18px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 8, color: '#fff', cursor: busy ? 'not-allowed' : 'pointer', background: busy ? '#9aa' : danger ? '#d9534f' : '#1e7e34' }}>
            {busy ? 'Working…' : label}
          </button>
        </div>
      </div>
    </div>
  )
}

const ADHOC_ERR = {
  missing_job: 'Pick a job.',
  invalid_date: 'Pick a valid date.',
  no_crew: 'Pick at least one crew member.',
  no_valid_crew: 'The selected crew could not be found.',
  job_not_found: 'That job no longer exists.',
  job_not_active: 'That job is no longer active.',
  not_authorized: 'You don’t have permission to add a report.',
  not_linked: 'Your login isn’t linked to your employee record yet — ask the office to link it.',
  no_pm: 'This job doesn’t have a PM assigned yet — please call the office.',
  no_foreman_identity: 'Your employee record has no name or email on file — please call the office.',
}

// "Add Report" form — create an ad-hoc report for a relocated/unscheduled crew.
// Pulls active jobs + active crew from /api/field-reports?action=adhoc-options,
// creates via action=create-adhoc, then drops the user into the office-entry
// editor for the new report (/report/approve?id=). Dedupe → offer to open.
function AddReportModal({ session, role, onClose }) {
  const isForeman = role === 'foreman'
  const [phase, setPhase] = useState('loading') // loading | ready | error
  const [loadErr, setLoadErr] = useState('')
  const [jobs, setJobs] = useState([])
  const [employees, setEmployees] = useState([])
  const [selfId, setSelfId] = useState(null)     // foreman only — who the caller is
  const [jobId, setJobId] = useState('')
  const [date, setDate] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }))
  const [crew, setCrew] = useState(() => new Set())
  const [foremanId, setForemanId] = useState('')
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [dupeId, setDupeId] = useState(null)     // openable existing report, or null when we can't show it
  const [dupeMsg, setDupeMsg] = useState(null)   // the amber duplicate text (server may override it)

  const token = async () => session?.access_token || (await supabase.auth.getSession()).data?.session?.access_token

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !busy) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, busy])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetch('/api/field-reports?action=adhoc-options', { headers: { Authorization: `Bearer ${await token()}` } })
        const data = await r.json()
        if (cancelled) return
        if (!r.ok || !data.ok) {
          // not_linked is actionable — never bury it under the generic message.
          setLoadErr(ADHOC_ERR[data.error] || 'Could not load jobs and crew. Please try again.')
          setPhase('error'); return
        }
        setJobs(data.jobs || []); setEmployees(data.employees || [])
        // Pre-check the foreman himself — the common case is that he worked with
        // his crew. He can untick it: being STAMPED as the report's foreman is
        // what makes it his, not appearing in the attendance list.
        if (data.self_employee_id != null) {
          setSelfId(data.self_employee_id)
          if ((data.employees || []).some(e => e.id === data.self_employee_id)) {
            setCrew(prev => new Set(prev).add(data.self_employee_id))
          }
        }
        setPhase('ready')
      } catch (e) { if (!cancelled) { setLoadErr('Could not load jobs and crew. Please try again.'); setPhase('error') } }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => { if (foremanId && !crew.has(Number(foremanId))) setForemanId('') }, [crew, foremanId])

  const clearMsg = () => { setErr(null); setDupeId(null); setDupeMsg(null) }
  const toggle = (id) => { clearMsg(); setCrew(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n }) }
  const filtered = employees.filter(e => !search.trim() || (e.name || '').toLowerCase().includes(search.trim().toLowerCase()))
  const crewList = employees.filter(e => crew.has(e.id))
  const canSubmit = !!jobId && !!date && crew.size > 0 && !busy

  // Prevention at the source: the same Mon–Sun ET week the server stamps
  // against (api/field-reports currentPayrollWeek). WARN, never block — a late
  // entry is sometimes exactly what's needed, it just has to be deliberate.
  const week = useMemo(() => { const t = todayET(); return { start: mondayOf(t), end: sundayOf(t) } }, [])
  const dateOutOfWeek = !!date && (date < week.start || date > week.end)

  async function create() {
    setBusy(true); clearMsg()
    try {
      const r = await fetch('/api/field-reports?action=create-adhoc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await token()}` },
        // A foreman sends NO foreman_id — the server stamps him from his linked
        // employee row and ignores the field anyway.
        body: JSON.stringify({
          job_id: Number(jobId), report_date: date, worker_ids: [...crew],
          ...(isForeman ? {} : { foreman_id: foremanId ? Number(foremanId) : null }),
        }),
      })
      const data = await r.json()
      // A duplicate the caller can't SEE comes back with existing_report_id null
      // and its own message — show that text with no dead-end link.
      if (r.status === 409 && data.error === 'duplicate_exists') {
        setDupeId(data.existing_report_id || null)
        setDupeMsg(data.message || 'A report already exists for this job and date.')
        setBusy(false); return
      }
      if (!r.ok || !data.ok) { setErr(data.message || ADHOC_ERR[data.error] || 'Could not create the report. Please try again.'); setBusy(false); return }
      gotoReportPage(`/report/approve?id=${encodeURIComponent(data.report_id)}`)
    } catch (e) { setErr('Could not create the report. Please try again.'); setBusy(false) }
  }

  const fieldLabel = { fontSize: 12, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 4 }
  const input = { width: '100%', boxSizing: 'border-box', padding: '9px 10px', fontSize: 14, border: '1px solid #cfd6dd', borderRadius: 8, fontFamily: 'inherit', background: '#fff', color: '#1f2430' }

  return (
    <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 520, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '18px 22px 12px' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#1e2d3d' }}>Add Report</div>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 3 }}>
            {isForeman
              ? 'Create a report for a day your crew worked that isn’t on your list yet, then fill in the hours and send it to your PM.'
              : 'Create a report for an unscheduled job a crew worked, then fill in the hours.'}
          </div>
        </div>

        <div style={{ padding: '4px 22px 16px', overflowY: 'auto' }}>
          {phase === 'loading' ? <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 14 }}>Loading…</div>
            : phase === 'error' ? <div style={{ padding: 24, textAlign: 'center', color: '#a12622', fontSize: 14 }}>{loadErr}</div>
              : (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label style={fieldLabel}>Job</label>
                    <select value={jobId} onChange={e => { setJobId(e.target.value); clearMsg() }} style={input}>
                      <option value="">— select an active job —</option>
                      {jobs.map(j => <option key={j.id} value={j.id}>{j.num} — {j.name}</option>)}
                    </select>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={fieldLabel}>Date</label>
                    <input type="date" value={date} onChange={e => { setDate(e.target.value); clearMsg() }} style={input} />
                    {dateOutOfWeek && (
                      <div style={{ marginTop: 6, background: '#fff8f0', borderLeft: '4px solid #e8820c', color: '#92510a', padding: '8px 10px', borderRadius: '0 6px 6px 0', fontSize: 12.5, lineHeight: 1.45 }}>
                        That’s outside this week ({mdOf(week.start)} – {mdOf(week.end)}). The report will be flagged for payroll review.
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={fieldLabel}>Crew — {crew.size} selected</label>
                    <input type="text" placeholder="Search crew…" value={search} onChange={e => setSearch(e.target.value)} style={{ ...input, marginBottom: 6 }} />
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e2e5e9', borderRadius: 8 }}>
                      {filtered.map(e => (
                        <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid #f2f4f6', cursor: 'pointer', fontSize: 13.5, color: '#1f2430' }}>
                          <input type="checkbox" checked={crew.has(e.id)} onChange={() => toggle(e.id)} />
                          <span>{e.name}{e.is_foreman ? ' ★' : ''}</span>
                          {!e.email && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#aaa' }}>no email</span>}
                        </label>
                      ))}
                      {filtered.length === 0 && <div style={{ padding: 12, color: '#888', fontSize: 13 }}>No matching crew.</div>}
                    </div>
                  </div>

                  {isForeman ? (
                    <div style={{ marginBottom: 4, fontSize: 12.5, color: '#6b7280' }}>
                      You’ll be recorded as the foreman on this report.
                    </div>
                  ) : (
                    <div style={{ marginBottom: 4 }}>
                      <label style={fieldLabel}>Foreman (optional)</label>
                      <select value={foremanId} onChange={e => setForemanId(e.target.value)} style={input} disabled={crewList.length === 0}>
                        <option value="">— none (no-foreman report) —</option>
                        {crewList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                    </div>
                  )}
                </>
              )}
        </div>

        {/* Banners live in the FIXED footer, never in the scrolling body: with a
            long crew list the body is already scrolled, so a banner rendered
            down there is below the fold and the click looks like it did
            nothing. */}
        <div style={{ borderTop: '1px solid #eef0f2' }}>
          {err && <div style={{ margin: '12px 22px 0', background: '#fdecec', borderLeft: '4px solid #d9534f', color: '#a12622', padding: '10px 12px', borderRadius: '0 6px 6px 0', fontSize: 13 }}>{err}</div>}
          {dupeMsg && (
            <div style={{ margin: '12px 22px 0', background: '#fff8f0', borderLeft: '4px solid #e8820c', color: '#92510a', padding: '10px 12px', borderRadius: '0 6px 6px 0', fontSize: 13 }}>
              {dupeMsg}{' '}
              {dupeId && (
                <button onClick={() => { gotoReportPage(`/report/approve?id=${encodeURIComponent(dupeId)}`) }}
                  style={{ background: 'none', border: 'none', color: '#1e5b8f', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 13 }}>
                  Open existing report
                </button>
              )}
            </div>
          )}
          <div style={{ padding: '12px 22px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} disabled={busy}
              style={{ padding: '9px 16px', fontSize: 14, border: '1px solid #cfd6dd', borderRadius: 8, background: '#fff', cursor: busy ? 'not-allowed' : 'pointer' }}>Cancel</button>
            <button onClick={create} disabled={!canSubmit}
              style={{ padding: '9px 18px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 8, color: '#fff', cursor: canSubmit ? 'pointer' : 'not-allowed', background: canSubmit ? '#1e7e34' : '#9bbfa6' }}>
              {busy ? 'Creating…' : 'Create & Fill In'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function FieldReportsView({ session, role, userEmail, userName, isMobile }) {
  const isAdmin = role === 'owner'
  // Foremen may create their own reports too (Patrick, 2026-08-20 — reverses the
  // 2026-08-03 call). The server is the real gate and additionally requires the
  // login to be LINKED to an employee record; this only decides the button.
  const canAddReport = role === 'owner' || role === 'road_super' || role === 'pm' || role === 'foreman'
  // Self-serve weekly payroll CSV: owner + office admin (Carla) only.
  const canExportCsv = role === 'owner' || role === 'admin'

  const [reports, setReports] = useState([])
  const [phase, setPhase] = useState('loading') // loading | ready | error
  const [errMsg, setErrMsg] = useState('')
  const [selectedId, setSelectedId] = useState(null)   // open detail
  const [approving, setApproving] = useState(false)
  const [actionMsg, setActionMsg] = useState(null)     // { kind, text }
  // Restored from sessionStorage when the user comes back from a report page —
  // see loadSavedFilters above. Lazy initializers, so each reads once at mount.
  const [filters, setFilters] = useState(() => loadSavedFilters()?.filters || { ...FILTER_DEFAULTS })
  const [quickRange, setQuickRange] = useState(() => loadSavedFilters()?.quickRange ?? '')  // which preset filled From/To ('' = custom)
  const [showDeleted, setShowDeleted] = useState(() => loadSavedFilters()?.showDeleted ?? false)
  const [checked, setChecked] = useState(() => new Set())
  const [menuOpen, setMenuOpen] = useState(null)       // report id whose ⋯ menu is open
  const [modal, setModal] = useState(null)             // confirm modal descriptor
  const [busy, setBusy] = useState(false)
  const [showAdd, setShowAdd] = useState(false)        // "Add Report" form open
  // Holds the week-ENDING Sunday (what the user sees); the Monday is derived
  // at call time for the API. Defaults to the Sunday that just passed.
  const [csvWeek, setCsvWeek] = useState(() => sundayOf(lastCompletedWeekMonday()))
  const [csvBusy, setCsvBusy] = useState(false)
  const [showProofing, setShowProofing] = useState(false)  // hours-proofing grid open
  // Server-decided (list response): owner, or a REOPEN_DELEGATES office admin.
  const [canReopenAny, setCanReopenAny] = useState(false)
  // Row checkboxes / select-all / ⋯ menu / bulk bar: owners, plus reopen delegates.
  const rowControls = isAdmin || canReopenAny

  // Download the approved-report PDF (stored in Supabase Storage; the API
  // generates it lazily for reports approved before 2026-07-23).
  const downloadReportPdf = async (r) => {
    try {
      const tok = session?.access_token || (await supabase.auth.getSession()).data?.session?.access_token
      const res = await fetch(`/api/field-reports?action=pdf&report_id=${encodeURIComponent(r.id)}`, {
        headers: { Authorization: `Bearer ${tok}` },
      })
      if (!res.ok) { setActionMsg({ kind: 'error', text: 'PDF download failed — try again in a moment.' }); return }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Field-Report-${r.job_number || r.id}-${r.report_date || ''}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    } catch (e) {
      setActionMsg({ kind: 'error', text: 'PDF download failed: ' + e.message })
    }
  }

  const C = useMemo(() => ({
    cardBg: isMobile ? 'var(--m-card)' : '#ffffff',
    border: isMobile ? '0.5px solid var(--m-border)' : '1px solid #e2e5e9',
    text: isMobile ? 'var(--m-text)' : '#1f2430',
    muted: isMobile ? 'var(--m-muted)' : '#6b7280',
    pageBg: isMobile ? 'transparent' : '#f4f4f4',
  }), [isMobile])

  const load = useCallback(async () => {
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const token = s?.access_token
      if (!token) { setErrMsg('Your session expired — please sign in again.'); setPhase('error'); return }
      const qs = showDeleted && isAdmin ? '&include_deleted=true' : ''
      const res = await fetch(`/api/field-reports?action=list${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        setErrMsg(data.error === 'invalid_token' || data.error === 'missing_token'
          ? 'Your session expired — please sign in again.'
          : 'Could not load field reports. Please try again.')
        setPhase('error'); return
      }
      setReports(data.reports || [])
      setCanReopenAny(data.can_reopen === true)
      setPhase('ready')
    } catch (e) {
      setErrMsg('Could not load field reports. Please try again.'); setPhase('error')
    }
  }, [showDeleted, isAdmin])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') load() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus) }
  }, [load])

  // Save the filter set on every change. Scoped ON PURPOSE to filters +
  // quickRange + showDeleted: the open report, the checkbox selection and the
  // CSV week are per-visit and must NOT come back with the user.
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_KEY, JSON.stringify({ ...filters, quickRange, showDeleted }))
    } catch (e) {
      // Storage unavailable (private mode / quota) — filters just don't persist.
    }
  }, [filters, quickRange, showDeleted])

  const jobOptions = useMemo(() => {
    const seen = new Map()
    for (const r of reports) {
      const key = r.job_number || r.job_name
      if (key && !seen.has(key)) seen.set(key, `${r.job_number || ''} ${r.job_name || ''}`.trim())
    }
    return [...seen.entries()]
  }, [reports])

  const filtered = useMemo(() => reports.filter(r => {
    if (filters.from && (r.report_date || '') < filters.from) return false
    if (filters.to && (r.report_date || '') > filters.to) return false
    if (filters.job !== 'all' && (r.job_number || r.job_name) !== filters.job) return false
    // Categorize by the SAME logic that renders the label (statusInfo.key) so
    // the filter and the badges always agree.
    if (filters.status !== 'all' && statusInfo(r).key !== filters.status) return false
    return true
  }), [reports, filters])

  // Quick-range dropdown → fills the From/To inputs in ONE setFilters call.
  // Purely a shortcut: `filtered` above is still the only thing that filters.
  function applyQuickRange(key) {
    setQuickRange(key)
    const r = quickRangeDates(key)
    if (!r) return                                    // the "Quick range…" placeholder
    setFilters(f => ({ ...f, from: r.from, to: r.to }))
  }
  // A manual From/To edit means the range is custom now, so the dropdown drops
  // back to the placeholder — which is also what makes re-picking the same
  // preset work (a <select> fires no onChange for the already-selected option).
  function setDateFilter(which, value) {
    setFilters(f => ({ ...f, [which]: value }))
    setQuickRange('')
  }

  const selected = reports.find(r => r.id === selectedId) || null
  // Foreman's own to-do count: his reports that nobody has submitted yet. The
  // list is already scoped to him server-side, so this is just a nudge — the
  // report rows themselves carry the same status badge.
  const owedByForeman = role === 'foreman'
    ? reports.filter(r => r.can_fill_in && !r.foreman_submitted_at).length
    : 0

  // ── selection helpers ───────────────────────────────────────────────────────
  const toggleCheck = (id) => setChecked(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n
  })
  const allFilteredChecked = filtered.length > 0 && filtered.every(r => checked.has(r.id))
  const toggleAll = () => setChecked(prev => {
    if (filtered.every(r => prev.has(r.id)) && filtered.length) return new Set()
    return new Set(filtered.map(r => r.id))
  })
  const selRows = reports.filter(r => checked.has(r.id))
  const bulk = {
    canReopen: selRows.length > 0 && selRows.every(r => isApproved(r) && !isDeleted(r)),
    canDelete: selRows.length > 0 && selRows.every(r => !isDeleted(r)),
    canRestore: selRows.length > 0 && selRows.every(r => isDeleted(r)),
  }

  // Download the weekly payroll CSV on demand (owner/admin). Calls the
  // session-authed endpoint with the Bearer token, then saves the streamed file.
  // The CSV is APPROVED-ONLY, so a pending report's hours are simply absent —
  // the X-Payroll-Pending-* headers are how that becomes visible instead of
  // silent. A clean week (0 pending) stays silent, as before.
  async function downloadPayrollCsv() {
    setCsvBusy(true); setActionMsg(null)
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const token = s?.access_token
      if (!token) { setActionMsg({ kind: 'err', text: 'Your session expired — please sign in again.' }); return }
      // csvWeek is the displayed week-ENDING Sunday; the API is still keyed on
      // the Monday, which is that Sunday minus 6 days = mondayOf(csvWeek).
      const ws = mondayOf(csvWeek)
      const res = await fetch(`/api/payroll/export-csv?week_start=${ws}`, { headers: { Authorization: `Bearer ${token}` } })
      // UNLINKED-WORKER HARD STOP: the server refuses the file while any approved
      // line has no employee number (TimeGenie can't post it — the man wouldn't
      // be paid). Name every line so the office can go fix it.
      if (res.status === 409) {
        let body = null
        try { body = await res.json() } catch (e) { /* fall through to the generic message */ }
        const list = Array.isArray(body?.unlinked) ? body.unlinked : []
        setActionMsg({
          kind: 'err',
          text: (
            <>
              <strong>Payroll CSV not downloaded — {list.length || 'some'} line{list.length === 1 ? '' : 's'} {list.length === 1 ? 'has' : 'have'} no employee number</strong>, so TimeGenie couldn’t pay {list.length === 1 ? 'it' : 'them'}:
              <ul style={{ margin: '6px 0 6px 18px', padding: 0 }}>
                {list.map((u, i) => (
                  <li key={i}>
                    {u.name || '(no name)'} — job {u.job_number || '?'} — {u.date}{u.hours ? ` — ${u.hours} h` : ''} (report{' '}
                    <a href={`/report/approve?id=${encodeURIComponent(u.report_id)}`} style={{ color: 'inherit', fontWeight: 600 }}>#{u.report_id}</a>)
                  </li>
                ))}
              </ul>
              Open each report and use <strong>Link to employee</strong> on that worker (an owner can reopen an approved report to edit it). If they aren’t in Foundation yet, add them, run the Foundation sync (Admin tab), then link. Then download again.
            </>
          ),
        })
        return
      }
      if (!res.ok) {
        setActionMsg({ kind: 'err', text: res.status === 403 ? 'You don’t have access to payroll exports.' : 'Could not generate the payroll CSV. Please try again.' })
        return
      }
      const pending = parseInt(res.headers.get('X-Payroll-Pending-Count') || '0', 10) || 0
      const pendingSummary = res.headers.get('X-Payroll-Pending-Summary') || ''
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payroll-week-ending-${csvWeek}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      if (pending > 0) {
        setActionMsg({
          kind: 'warn',
          text: `CSV downloaded. ${pending} report${pending === 1 ? '' : 's'} still pending approval — ${pending === 1 ? 'its' : 'their'} hours are NOT included${pendingSummary ? `: ${pendingSummary}` : ''}.`,
        })
      }
    } catch (e) {
      setActionMsg({ kind: 'err', text: 'Could not generate the payroll CSV. Please try again.' })
    } finally {
      setCsvBusy(false)
    }
  }

  // ── admin actions ───────────────────────────────────────────────────────────
  async function adminPost(action, body) {
    const { data: { session: s } } = await supabase.auth.getSession()
    const res = await fetch(`/api/payroll/admin/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.access_token}` },
      body: JSON.stringify(body),
    })
    return res
  }

  async function runAction(action, ids) {
    setBusy(true)
    console.error(`ADMIN: ${action} requested by ${userEmail} for report_ids: [${ids.join(', ')}]`)
    try {
      const res = await adminPost(action, { report_ids: ids })
      const data = await res.json().catch(() => ({}))
      console.error(`ADMIN: ${action} result: ${JSON.stringify(data)}`)
      if (res.status === 403) { setActionMsg({ kind: 'err', text: 'Admin access required.' }); return }
      if (!res.ok) { setActionMsg({ kind: 'err', text: `Could not ${action} report(s).` }); return }
      const n = data.reopened ?? data.deleted ?? data.restored ?? ids.length
      const skipped = (data.skipped || []).length
      setActionMsg({ kind: 'ok', text: `${action[0].toUpperCase()}${action.slice(1)} complete: ${n} report(s)${skipped ? `, ${skipped} skipped` : ''}.` })
      setChecked(new Set())
      await load()
    } catch (e) {
      setActionMsg({ kind: 'err', text: `Could not ${action} report(s).` })
    } finally {
      setBusy(false)
      setModal(null)
    }
  }

  async function handleEdit(id) {
    setMenuOpen(null)
    const r = reports.find(x => x.id === id)
    // Non-approved reports edit + finalize through the AUTHENTICATED approve /
    // office-entry view: it records the real logged-in role and routes an
    // unsubmitted report through office-submit (office_entry) and a submitted one
    // through edit-in-place + approve (direct/escalation). Only already-approved
    // reports use the admin token editor, to correct them after the fact.
    if (r && !isApproved(r) && !isDeleted(r)) {
      // &edit=1 opens a submitted report straight in edit mode (one click, not two).
      gotoReportPage(`/report/approve?id=${encodeURIComponent(id)}&edit=1`)
      return
    }
    try {
      const res = await adminPost('edit-token', { report_id: id })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.token) {
        gotoReportPage(`/report/edit?token=${encodeURIComponent(data.token)}`)
      } else {
        setActionMsg({ kind: 'err', text: 'Could not open the editor for this report.' })
      }
    } catch (e) {
      setActionMsg({ kind: 'err', text: 'Could not open the editor for this report.' })
    }
  }

  // confirm modal builders
  const askReopen = (ids) => setModal({
    action: 'reopen', ids, danger: true, label: ids.length > 1 ? `Reopen ${ids.length} Reports` : 'Reopen',
    title: ids.length > 1 ? `Reopen ${ids.length} Reports?` : 'Reopen Report?',
    body: (ids.length > 1
      ? `This sets ${ids.length} reports back to pending. The job's PM will need to re-approve them.`
      : "This sets the report back to pending. The job's PM will need to re-approve it.")
      + (isAdmin ? '' : ' Patrick will be emailed that you reopened it.'),
  })
  const askDelete = (ids) => setModal({
    action: 'delete', ids, danger: true, label: ids.length > 1 ? `Delete ${ids.length} Reports` : 'Delete',
    title: ids.length > 1 ? `Delete ${ids.length} Reports?` : 'Delete Report?',
    body: ids.length > 1
      ? `${ids.length} reports will be hidden from all views and CSVs. You can restore them later from the “Show Deleted” view.`
      : 'This report will be hidden from all daily reports views, CSV exports, and escalation emails. You can restore it later from the “Show Deleted” view. Continue?',
  })
  const askRestore = (ids) => setModal({
    action: 'restore', ids, danger: false, label: ids.length > 1 ? `Restore ${ids.length} Reports` : 'Restore',
    title: ids.length > 1 ? `Restore ${ids.length} Reports?` : 'Restore Report?',
    body: ids.length > 1 ? `${ids.length} reports will return to active status.` : 'This will return the report to active status. Continue?',
  })

  // ── states ──────────────────────────────────────────────────────────────────
  if (phase === 'loading') return <div style={{ padding: 32, textAlign: 'center', color: C.muted, fontSize: 14 }}>Loading field reports…</div>
  if (phase === 'error') {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: C.muted, fontSize: 14 }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
        <div style={{ marginBottom: 12 }}>{errMsg}</div>
        <button onClick={() => { setPhase('loading'); load() }} style={{ padding: '8px 18px', border: C.border, borderRadius: 8, background: C.cardBg, cursor: 'pointer', fontSize: 13 }}>Retry</button>
      </div>
    )
  }

  // ── detail view ─────────────────────────────────────────────────────────────
  if (selected) {
    const st = statusInfo(selected)
    // Inbound-first reports carry report_data without a completed call, so show
    // the body whenever there's data (not gated on call_status).
    const hasData = !!selected.report_data
    // For a no_useful_data call: a transcript / real duration means it connected.
    const callTranscript = selected.transcript_english || selected.raw_transcript || ''
    const callConnected = !!callTranscript || (selected.call_duration_seconds || 0) >= 15
    const approved = isApproved(selected)
    const submitted = !!selected.foreman_submitted_at
    const windowOpen = selected.foreman_edit_window_ends_at ? new Date(selected.foreman_edit_window_ends_at) > new Date() : false
    const pmBlocked = role === 'pm' && windowOpen
    const showApprove = submitted && !approved && !isDeleted(selected) && hasData && selected.can_approve && role !== 'foreman'
    // Office entry: fill-and-finalize an UNSUBMITTED report (e.g. no-foreman job).
    const showOfficeEntry = !submitted && !approved && !isDeleted(selected) && hasData && selected.can_office_enter
    // Foreman self-service: this report is HIS and the PM hasn't approved it, so
    // he can fill it in (or correct what he phoned in). Server-decided flag.
    const showForemanEntry = !approved && !isDeleted(selected) && hasData && selected.can_fill_in

    return (
      <div style={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: C.pageBg }}>
        <div style={{ maxWidth: 700, margin: '0 auto', padding: isMobile ? '10px 10px 0' : '14px 16px 0' }}>
          <button onClick={() => { setSelectedId(null); setActionMsg(null) }}
            style={{ background: 'none', border: 'none', color: isMobile ? 'var(--m-blue)' : '#1e5b8f', fontSize: 14, cursor: 'pointer', padding: '6px 0', marginBottom: 6 }}>
            ‹ Back to reports
          </button>
        </div>
        <div style={{ maxWidth: 700, margin: '0 auto', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: isMobile ? 'none' : '0 1px 4px rgba(0,0,0,.12)', marginBottom: isMobile ? 0 : 16 }}>
          <ReportHeader report={selected} />
          <div style={sx.body}>
            <div style={{ ...sx.banner(st.bg, approved ? '#1e7e34' : '#e8820c', st.fg) }}>
              {isDeleted(selected)
                ? <>🗑️ <strong>Deleted</strong>{selected.deleted_at ? ` ${fmtWhen(selected.deleted_at)}` : ''}{selected.deleted_by ? ` by ${selected.deleted_by}` : ''}.</>
                : approved
                  ? <>✅ <strong>Approved</strong>{selected.pm_approved_at ? ` ${fmtWhen(selected.pm_approved_at)}` : ''}{selected.pm_approved_by ? ` by ${selected.pm_approved_by}` : ''}{selected.approved_by_role ? ` (${selected.approved_by_role})` : ''}.</>
                  : <>🕒 <strong>{st.label}</strong>{pmBlocked && selected.foreman_edit_window_ends_at ? ` — foreman can edit until ${fmtEditWindow(selected.foreman_edit_window_ends_at)}.` : '.'}</>}
            </div>
            {approved && (
              <div style={{ margin: '0 0 14px' }}>
                <button onClick={() => downloadReportPdf(selected)}
                  style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, border: '1px solid #1e7e34', borderRadius: 8, background: '#fff', color: '#1e7e34', cursor: 'pointer' }}>
                  ⬇ Download PDF
                </button>
              </div>
            )}
            <AuditTrail report={selected} />
            {selected.call_status === 'no_useful_data' && callTranscript && (
              <details style={{ margin: '0 0 14px' }}>
                <summary style={{ cursor: 'pointer', color: '#1e5b8f', fontSize: 13 }}>View call transcript</summary>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12.5, color: '#444', background: '#f7f8fa', border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px', marginTop: 8, fontFamily: 'inherit' }}>{callTranscript}</pre>
              </details>
            )}
            {hasData
              ? <ReportBodySections report={selected} form={normalizeReportData(selected.report_data)} ro={true} set={() => {}} costCodes={[]} />
              : (
                <div style={{ textAlign: 'center', padding: '36px 16px', color: '#888' }}>
                  <div style={{ fontSize: 30, marginBottom: 8 }}>{selected.call_status === 'no_useful_data' && callConnected ? '🕒' : '⏳'}</div>
                  <div style={{ fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    {selected.call_status === 'completed'
                      ? 'Report data unavailable'
                      : selected.call_status === 'no_useful_data'
                        ? (callConnected
                            ? `Call connected${fmtDur(selected.call_duration_seconds) ? ` (${fmtDur(selected.call_duration_seconds)})` : ''} — no report completed`
                            : 'No data — call did not connect')
                        : 'Awaiting call completion'}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {selected.call_status === 'completed'
                      ? 'The call completed but the report could not be extracted.'
                      : selected.call_status === 'no_useful_data'
                        ? (callConnected
                            ? 'The foreman reached Sarah but hung up before a report was completed. No report data was captured.'
                            : 'The call did not connect, so nothing was captured.')
                        : 'This report will populate once the foreman’s call is completed.'}
                  </div>
                </div>
              )}
          </div>
        </div>
        {showApprove && (
          <div style={isMobile
            ? { position: 'sticky', bottom: 0, background: '#fff', borderTop: '0.5px solid var(--m-border)', padding: '12px 14px', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }
            : { maxWidth: 700, margin: '0 auto 24px', padding: '0 16px' }}>
            {actionMsg && <div style={{ ...msgBanner(actionMsg.kind), marginTop: 0, marginBottom: 10 }}>{actionMsg.text}</div>}
            <button onClick={handleApprove} disabled={approving || pmBlocked} style={sx.actionBtn(approving || pmBlocked)}>
              {approving ? 'Approving…' : pmBlocked ? 'Approval available after foreman window closes' : 'Approve Report'}
            </button>
            {/* Same authenticated editor the approval email links to — edit-in-place
                (save-edit, attributed to the logged-in user), then approve there. */}
            <button
              onClick={() => { gotoReportPage(`/report/approve?id=${encodeURIComponent(selected.id)}&edit=1`) }}
              disabled={approving}
              style={{ ...sx.actionBtn(approving, '#2563eb', '#9bb4e6'), marginTop: 10 }}>
              Edit Report
            </button>
            <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', marginTop: 8 }}>
              Need to fix hours or details first? <strong>Edit Report</strong> opens the full editor — your changes are saved under your name, then you can approve.
            </div>
          </div>
        )}
        {showOfficeEntry && (
          <div style={isMobile
            ? { position: 'sticky', bottom: 0, background: '#fff', borderTop: '0.5px solid var(--m-border)', padding: '12px 14px', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }
            : { maxWidth: 700, margin: '0 auto 24px', padding: '0 16px' }}>
            <button onClick={() => { gotoReportPage(`/report/approve?id=${encodeURIComponent(selected.id)}`) }} style={sx.actionBtn(false)}>
              Enter &amp; Submit Report
            </button>
            <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', marginTop: 8 }}>
              Opens the editor to fill in the crew’s hours, then submits and releases the report to payroll under your name.
            </div>
          </div>
        )}
        {showForemanEntry && (
          <div style={isMobile
            ? { position: 'sticky', bottom: 0, background: '#fff', borderTop: '0.5px solid var(--m-border)', padding: '12px 14px', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }
            : { maxWidth: 700, margin: '0 auto 24px', padding: '0 16px' }}>
            <button onClick={() => { gotoReportPage(`/report/approve?id=${encodeURIComponent(selected.id)}`) }} style={sx.actionBtn(false)}>
              {submitted ? 'Edit & Resubmit' : 'Fill In & Submit Report'}
            </button>
            <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', marginTop: 8 }}>
              {submitted
                ? 'You already submitted this one. You can still fix it until your PM approves it.'
                : 'Opens the form to fill in your crew’s hours and the day’s work, then sends it to your PM.'}
            </div>
          </div>
        )}
        {!showApprove && !showOfficeEntry && !showForemanEntry && actionMsg && (
          <div style={{ maxWidth: 700, margin: '0 auto 20px', padding: '0 16px' }}>
            <div style={{ ...msgBanner(actionMsg.kind), marginTop: 0 }}>{actionMsg.text}</div>
          </div>
        )}
      </div>
    )
  }

  // ── list view ───────────────────────────────────────────────────────────────
  const inputStyle = { padding: '8px 10px', fontSize: 13, border: C.border, borderRadius: 8, background: C.cardBg, color: C.text, fontFamily: 'inherit' }
  const selIds = [...checked]

  function rowMenuItems(r) {
    // Reopen delegate (not an owner): Download PDF + Reopen on approved rows, nothing else.
    if (!isAdmin) {
      if (isDeleted(r) || !isApproved(r)) return []
      return [
        { label: 'Download PDF', on: () => { setMenuOpen(null); downloadReportPdf(r) } },
        { label: 'Reopen', on: () => { setMenuOpen(null); askReopen([r.id]) } },
      ]
    }
    if (isDeleted(r)) return [{ label: 'Restore', on: () => { setMenuOpen(null); askRestore([r.id]) } }]
    const items = []
    if (isApproved(r)) {
      items.push({ label: 'Download PDF', on: () => { setMenuOpen(null); downloadReportPdf(r) } })
      items.push({ label: 'Reopen', on: () => { setMenuOpen(null); askReopen([r.id]) } })
    }
    items.push({ label: 'Edit', on: () => handleEdit(r.id) })
    items.push({ label: 'Delete', danger: true, on: () => { setMenuOpen(null); askDelete([r.id]) } })
    return items
  }

  return (
    <div style={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: isMobile ? 12 : 16, background: C.pageBg }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        {(canExportCsv || canAddReport) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
            {canExportCsv ? (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <label style={{ fontSize: 12, color: C.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>Week ending (Sun)
                  <input type="date" value={csvWeek} onChange={e => { if (e.target.value) setCsvWeek(sundayOf(e.target.value)) }} style={inputStyle} />
                </label>
                <button onClick={downloadPayrollCsv} disabled={csvBusy}
                  style={{ padding: '9px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: '#1e2d3d', color: '#fff', cursor: csvBusy ? 'not-allowed' : 'pointer', opacity: csvBusy ? 0.7 : 1 }}>
                  {csvBusy ? 'Preparing…' : '⬇ Download payroll CSV'}
                </button>
                <button onClick={() => setShowProofing(true)}
                  style={{ padding: '9px 16px', fontSize: 13, fontWeight: 600, border: '1px solid #1e2d3d', borderRadius: 8, background: '#fff', color: '#1e2d3d', cursor: 'pointer' }}>
                  ⊞ Proof hours
                </button>
              </div>
            ) : <div />}
            {canAddReport && (
              <button onClick={() => setShowAdd(true)}
                style={{ padding: '9px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: '#1e7e34', color: '#fff', cursor: 'pointer' }}>
                + Add Report
              </button>
            )}
          </div>
        )}
        {role === 'foreman' && (
          <div style={{ ...sx.banner(owedByForeman ? '#fff4e5' : '#eaf6ec', owedByForeman ? '#e8820c' : '#1e7e34', owedByForeman ? '#92510a' : '#1e5128'), marginTop: 0 }}>
            {owedByForeman
              ? <><strong>{owedByForeman} report{owedByForeman === 1 ? '' : 's'} still need{owedByForeman === 1 ? 's' : ''} your hours.</strong> Tap one below to fill it in.</>
              : <>✅ <strong>You’re all caught up.</strong> Every report assigned to you has been submitted.</>}
          </div>
        )}
        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
          <label style={{ fontSize: 12, color: C.muted, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 120 }}>Range
            <select value={quickRange} onChange={e => applyQuickRange(e.target.value)} style={inputStyle}>
              {QUICK_RANGES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select></label>
          <label style={{ fontSize: 12, color: C.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>From
            <input type="date" value={filters.from} onChange={e => setDateFilter('from', e.target.value)} style={inputStyle} /></label>
          <label style={{ fontSize: 12, color: C.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>To
            <input type="date" value={filters.to} onChange={e => setDateFilter('to', e.target.value)} style={inputStyle} /></label>
          <label style={{ fontSize: 12, color: C.muted, display: 'flex', flexDirection: 'column', gap: 3 }}>Status
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
              <option value="all">All</option>
              <option value="no_foreman">No foreman — needs office entry</option>
              <option value="pending_foreman">Pending Foreman Submit</option>
              <option value="pending_pm">Pending PM Review</option>
              <option value="escalated">Escalated</option>
              <option value="approved">Approved</option>
            </select></label>
          <label style={{ fontSize: 12, color: C.muted, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 140 }}>Job
            <select value={filters.job} onChange={e => setFilters(f => ({ ...f, job: e.target.value }))} style={inputStyle}>
              <option value="all">All jobs</option>{jobOptions.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select></label>
          {isAdmin && (
            <label style={{ fontSize: 12, color: C.muted, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <input type="checkbox" checked={showDeleted} onChange={e => { setShowDeleted(e.target.checked); setChecked(new Set()) }} />
              Show deleted reports
            </label>
          )}
          <div style={{ marginLeft: 'auto', fontSize: 12, color: C.muted, marginBottom: 6 }}>{filtered.length} report{filtered.length === 1 ? '' : 's'}</div>
        </div>

        {actionMsg && (
          <div style={{ ...msgBanner(actionMsg.kind), marginTop: 0 }}>{actionMsg.text}</div>
        )}

        {/* Admin bulk bar */}
        {rowControls && selIds.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#1e2d3d', color: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{selIds.length} selected</span>
            {bulk.canReopen && <button onClick={() => askReopen(selIds)} style={bulkBtn('#e8820c')}>Reopen Selected</button>}
            {isAdmin && bulk.canDelete && <button onClick={() => askDelete(selIds)} style={bulkBtn('#d9534f')}>Delete Selected</button>}
            {isAdmin && bulk.canRestore && <button onClick={() => askRestore(selIds)} style={bulkBtn('#1e7e34')}>Restore Selected</button>}
            <button onClick={() => setChecked(new Set())} style={{ ...bulkBtn('transparent'), border: '1px solid #557' }}>Clear</button>
          </div>
        )}

        {/* Select-all */}
        {rowControls && filtered.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.muted, padding: '0 4px 8px' }}>
            <input type="checkbox" checked={allFilteredChecked} onChange={toggleAll} /> Select all ({filtered.length})
          </label>
        )}

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 16px', color: C.muted, fontSize: 14 }}>
            No field reports{reports.length ? ' match these filters' : ' yet'}.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 8 }}>
            {filtered.map(r => {
              const st = statusInfo(r)
              const dur = fmtDur(r.call_duration_seconds)
              const del = isDeleted(r)
              // Stamped at creation, never recomputed — see reportAudit.js.
              const late = outOfWeekEntry(r)
              return (
                <div key={r.id} style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                  {rowControls && (
                    <label onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', paddingLeft: 2 }}>
                      <input type="checkbox" checked={checked.has(r.id)} onChange={() => toggleCheck(r.id)} />
                    </label>
                  )}
                  <button onClick={() => { setSelectedId(r.id); setActionMsg(null) }}
                    style={{
                      flex: 1, textAlign: 'left', background: C.cardBg, border: C.border, borderRadius: 12,
                      padding: isMobile ? '14px 16px' : '12px 16px', cursor: 'pointer', display: 'flex',
                      alignItems: 'center', gap: 12, minHeight: isMobile ? 64 : 'auto', width: '100%',
                      fontFamily: 'inherit', opacity: del ? 0.6 : 1,
                    }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: isMobile ? 15 : 14, color: C.text, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: del ? 'line-through' : 'none' }}>
                        {r.job_number ? `${r.job_number} — ` : ''}{r.job_name || 'Unknown job'}
                      </div>
                      <div style={{ fontSize: 12, color: C.muted }}>
                        {fmtDate(r.report_date)} · {r.foreman_name || 'Unknown foreman'}{dur ? ` · ${dur} call` : ''}
                        {del && r.deleted_at ? ` · deleted ${fmtDate(r.deleted_at.slice(0, 10))}${r.deleted_by ? ` by ${r.deleted_by}` : ''}` : ''}
                      </div>
                    </div>
                    {late && !del && (
                      <span title={`Entered for ${late.report_date || 'a past date'}, outside the payroll week ${late.payroll_week_start || ''}–${late.payroll_week_end || ''}`}
                        style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, padding: '5px 9px', borderRadius: 20, background: '#fdecec', color: '#a12622', whiteSpace: 'nowrap' }}>
                        ⚠ Late entry
                      </span>
                    )}
                    <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 20, background: st.bg, color: st.fg, whiteSpace: 'nowrap' }}>
                      {del ? 'DELETED' : st.label}
                    </span>
                    <span style={{ flexShrink: 0, color: C.muted, fontSize: 18 }}>›</span>
                  </button>
                  {rowControls && (rowMenuItems(r).length === 0 ? <div style={{ width: 34 }} /> : (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <button onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === r.id ? null : r.id) }}
                        title="Actions"
                        style={{ border: C.border, background: C.cardBg, borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 16, color: C.text }}>⋯</button>
                      {menuOpen === r.id && (
                        <>
                          <div onClick={() => setMenuOpen(null)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                          <div style={{ position: 'absolute', right: 0, top: 38, zIndex: 51, background: '#fff', border: '1px solid #e2e5e9', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,.15)', minWidth: 130, overflow: 'hidden' }}>
                            {rowMenuItems(r).map((it, i) => (
                              <button key={i} onClick={it.on}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', fontSize: 13, border: 'none', background: '#fff', cursor: 'pointer', color: it.danger ? '#d9534f' : '#1e2d3d' }}>
                                {it.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {modal && (
        <ConfirmModal
          title={modal.title} body={modal.body} label={modal.label} danger={modal.danger} busy={busy}
          onCancel={() => setModal(null)}
          onConfirm={() => runAction(modal.action, modal.ids)}
        />
      )}
      {showAdd && <AddReportModal session={session} role={role} onClose={() => setShowAdd(false)} />}
      {showProofing && (
        <HoursProofingModal weekStart={mondayOf(csvWeek)} weekEnding={csvWeek} onClose={() => setShowProofing(false)} />
      )}
    </div>
  )

  function bulkBtn(bg) {
    return { padding: '7px 12px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6, color: '#fff', background: bg, cursor: 'pointer' }
  }

  async function handleApprove() {
    if (!selected) return
    setApproving(true); setActionMsg(null)
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const res = await fetch('/api/field-reports?action=approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.access_token}` },
        body: JSON.stringify({ report_id: selected.id }),
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        if (data.error === 'unlinked_workers') {
          const names = (data.unlinked || []).map(u => u.name).filter(Boolean)
          setActionMsg({
            kind: 'err',
            text: (
              <>
                <strong>Can’t approve: {names.length} worker{names.length === 1 ? '' : 's'} {names.length === 1 ? 'isn’t' : 'aren’t'} linked to a payroll employee number</strong>{names.length ? ` (${names.join(', ')})` : ''}.{' '}
                <a href={`/report/approve?id=${encodeURIComponent(selected.id)}`} style={{ color: 'inherit', fontWeight: 600 }}>Open the report to link them</a>, then approve.
              </>
            ),
          })
          return
        }
        const map = {
          edit_window_open: 'The foreman can still edit this report — it can be approved after their window closes.',
          not_authorized: 'You don’t have permission to approve this report.',
          report_not_found: 'This report no longer exists.',
          report_deleted: 'This report has been removed.',
        }
        setActionMsg({ kind: 'err', text: map[data.error] || 'Approval failed. Please try again.' }); return
      }
      const approvedAt = data.approved_at || new Date().toISOString()
      setReports(prev => prev.map(r => r.id === selected.id ? { ...r, pm_approval_status: 'approved', pm_approved_at: approvedAt, pm_approved_by: userEmail, approved_by_role: role } : r))
      setActionMsg({ kind: 'ok', text: data.already_approved ? 'This report was already approved.' : 'Report approved. Stakeholders have been notified.' })
    } catch (e) {
      setActionMsg({ kind: 'err', text: 'Approval failed. Please try again.' })
    } finally {
      setApproving(false)
    }
  }
}
